import React, { FC, useEffect, useState } from 'react'
import { CurrentBookingsTable } from '../CurrentBookingsTable'
import useBookingModal, { UseBookingsHook } from './hooks/useBookingModal'
import createBookingColumns from './createColumns'
import css from './bookingsModal.module.scss'
import api, { IBooking } from '@api'
import { BaseTableFilters } from '../CurrentBookingsTable/types'
import { ActiveTab } from './types'
import { Dialog, Toaster, Icon, IconButton } from '@ui'
import { Position, Tab, Tabs } from '@blueprintjs/core'
import { useFloor } from '@providers/mapProvider'
import useSettings from 'components/NewSettings/hooks/useSettings'
import { DateUtil, TIME, isMultiDayBooking, isRecurringBooking } from '@utils'
import BookingCalendarPopup from 'components/BookingCalendarPopup/BookingCalendarPopup'
import { useBookingDesk } from '@providers'
import { handleGraphQLErrors } from '@utils/error'
import {
  BookingNoteCard,
  MAX_CHARACTERS_FOR_NOTE,
} from 'components/AdminFloorPlan/Cards/SeatableSpaceCard'
import { Button } from '@runeffective/evo-design-system'

const BookingsModal: FC<React.PropsWithChildren<{ isOpen: boolean; onClose: () => void }>> = ({
  isOpen,
  onClose,
}) => {
  const bookingsDataHook = useBookingModal()
  const floor = useFloor()
  const settings = useSettings()
  const bookingDeskHook = useBookingDesk()

  const [activeTab, setActiveTab] = useState(ActiveTab.CURRENT)
  const [calendarBooking, setCalendarBooking] = useState<IBooking | null>(null)
  const [note, setNote] = useState('')

  const isCurrentTab = activeTab === ActiveTab.CURRENT
  const defaultStyle = { padding: '0px 20px' }
  const activeStyle = { color: 'var(--primary-color)' }

  useEffect(() => {
    const isUpcomingBooking = activeTab === ActiveTab.CURRENT
    bookingsDataHook.updateItems(isUpcomingBooking)
    bookingsDataHook.startDataSync(isUpcomingBooking)
  }, [activeTab])
  useEffect(() => {
    bookingsDataHook.startDataSync(isCurrentTab)
  }, [isOpen === true])
  useEffect(() => {
    setNote(calendarBooking?.note || '')
  }, [calendarBooking])

  const onDialogClose = () => {
    bookingsDataHook.stopDataSync()
    onClose()
  }

  const columns = createBookingColumns({
    cancelBooking: (bookingId: string | undefined) => {
      bookingId &&
        api.booking.cancel.bookings({ bookingIds: [bookingId] }).then(() => {
          Toaster.success({
            message: 'Booking cancelled',
          })
          bookingsDataHook.fetchBookings()
        })
    },
    isCurrentBookings: isCurrentTab,
    spaceLocate: floor.spaceLocate,
    setIsBookDeskPage: floor.setIsBookDeskPage,
    isBookDeskPage: floor.isBookDeskPage,
    closeModal: onDialogClose,
    settings,
    editBooking: (booking: IBooking) => setCalendarBooking(booking),
  })
  const timezone = calendarBooking?.space.floor.building?.timezone || DateUtil.defaultTz()

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onDialogClose}
      canOutsideClickClose
      body={
        <div className={css.wrapper}>
          {calendarBooking ? (
            <BookingCalendarPopup
              value=" "
              title={
                <div>
                  <Button
                    className="button-content"
                    size="small"
                    startIcon={
                      <Icon name="fa-regular fa-arrow-left" style={{ marginRight: '8px' }} />
                    }
                    layout="borderless"
                    style={{ display: 'inline', textAlign: 'right', marginRight: '80px' }}
                    onClick={() => onDialogClose()}>
                    Back
                  </Button>
                  Edit Booking
                </div>
              }
              isOpen
              selected={[
                DateUtil.tz(calendarBooking.startDate, timezone),
                DateUtil.tz(calendarBooking.endDate, timezone),
              ]}
              updateHandler={(dates) => {
                const {
                  id,
                  // bookingSchedule: { id: bookingScheduleId },
                  space: { id: spaceId },
                  employee: { id: employeeId },
                } = calendarBooking
                const isMultiDay = isMultiDayBooking(calendarBooking)
                const isRecurrence = isRecurringBooking(calendarBooking)
                const startEndDates = { startDate: dates[0], endDate: dates[1] || dates[0] }
                if (isMultiDay || isRecurrence) {
                  bookingDeskHook
                    .bookRecurringDesk({ ...startEndDates, note, spaceId, employeeId })
                    .then(() => {
                      setCalendarBooking(null)
                    })
                    .catch(handleGraphQLErrors)
                } else {
                  api.booking.update
                    .updateBooking({ id, note, ...startEndDates })
                    .then(() => {
                      bookingsDataHook.fetchBookings()
                      setCalendarBooking(null)
                    })
                    .catch(handleGraphQLErrors)
                }
              }}
              onClose={() => setCalendarBooking(null)}
              timezone={timezone}>
              <div>
                <div>Note</div>
                <BookingNoteCard
                  width={450}
                  bookingNote={note}
                  setBookingNote={setNote}
                  maxCharLength={MAX_CHARACTERS_FOR_NOTE}
                />
              </div>
            </BookingCalendarPopup>
          ) : (
            <Tabs
              className={css.tabs}
              selectedTabId={activeTab}
              onChange={(tab: ActiveTab) => setActiveTab(tab)}>
              <Tab
                id="current"
                title="Upcoming Bookings"
                style={{ marginRight: 0, ...defaultStyle, ...(isCurrentTab ? activeStyle : {}) }}
                panel={
                  <div>
                    <CurrentBookingsTable<BaseTableFilters, UseBookingsHook>
                      headerTopWithoutFilter={0}
                      columns={columns}
                      bookingHook={bookingsDataHook}
                    />
                  </div>
                }
                className={css.tab}
              />
              <Tab
                id="past"
                title="Past Bookings"
                style={{
                  ...defaultStyle,
                  ...(!isCurrentTab ? activeStyle : {}),
                }}
                panel={
                  <div className={css.wrapper}>
                    <CurrentBookingsTable<BaseTableFilters, UseBookingsHook>
                      headerTopWithoutFilter={0}
                      columns={columns}
                      bookingHook={bookingsDataHook}
                    />
                  </div>
                }
                className={css.tab}
              />
              <div style={{ position: 'absolute', right: 3 }}>
                <IconButton
                  style={{ marginBottom: 4 }}
                  minimal
                  small
                  iconName="times"
                  onClick={() => {
                    onClose?.()
                  }}
                />
              </div>
            </Tabs>
          )}
        </div>
      }
    />
  )
}

export default BookingsModal
